<template>
	<form class="receipts-form" enctype="multipart/form-data" @submit.prevent="submitForm" ref="form">
		<input type="hidden" name="_method" value="PUT">
		<input type="hidden" name="qr_data" :value="data.qr_data || ''">
		<div class="icon">
			<i class="fa-thin fa-receipt"></i>
		</div>
		<errors-list :errors="errors"/>
		<div class="form-row">
			<default-field-box type="text" name="name" :label="$t('forms.receipt_name')" :required="true"
			                   :value="data.name" :disabled="!isEditing"/>
		</div>
		<div class="form-row">
			<field-box name="merchant_name" type="text" :label="$t('forms.merchant_name')" prefix="edit"
			           :value="data.merchant_name" :disabled="!isEditing"/>
		</div>
		<div class="form-row two-columns">
			<field-box name="date" type="date" :label="$t('forms.purchase_date')" prefix="edit"
			           :value="data.date" :disabled="!isEditing"/>
			<field-box name="currency" type="select" :label="$t('forms.currency')" prefix="edit"
			           :options="currencyOptions" :value="data.currency || defaultCurrency" :disabled="!isEditing"/>
		</div>
		<div class="form-row two-columns">
			<field-box name="total_price" type="text" :label="$t('forms.total_price')" prefix="edit"
			           :value="data.total_price" :disabled="!isEditing"/>
			<field-box name="vat_amount" type="text" :label="$t('forms.vat_amount')" prefix="edit"
			           :value="data.vat_amount" :disabled="!isEditing"/>
		</div>
		<div class="items-fieldset" v-if="items.length">
			<div class="items-header">
				<small>{{ $t('forms.item_name') }}</small>
				<small>{{ $t('forms.item_price') }}</small>
				<small>{{ $t('forms.item_quantity') }}</small>
				<small>{{ $t('forms.item_vat_rate') }}</small>
			</div>
			<div class="item-row" v-for="(item, index) in items" :key="index">
				<input type="text" :name="`items[${index}][name]`" v-model="item.name" :disabled="!isEditing" :placeholder="$t('forms.item_name')">
				<input type="number" :name="`items[${index}][price]`" v-model="item.price" step="any" inputmode="decimal" :disabled="!isEditing" :placeholder="$t('forms.item_price')">
				<input type="number" :name="`items[${index}][quantity]`" v-model="item.quantity" step="any" inputmode="decimal" :disabled="!isEditing" :placeholder="$t('forms.item_quantity')">
				<input type="number" :name="`items[${index}][vat_rate]`" v-model="item.vat_rate" step="any" inputmode="decimal" :disabled="!isEditing" :placeholder="$t('forms.item_vat_rate')">
				<button type="button" class="remove-item-btn" @click.stop="removeItem(index)" v-if="isEditing">
					<i class="fa-light fa-trash"></i>
				</button>
			</div>
		</div>
		<div class="form-row" v-if="isEditing">
			<button type="button" class="button style-1 add-item-btn" @click="addItem">
				<span class="inner">{{ $t('forms.add_item') }}</span>
			</button>
		</div>
		<div class="form-row qr-preview" v-if="data.qr_data">
				<qr-code-box :text="data.qr_data"/>
			</div>
		<div class="attachments-container" v-if="data.attachments && data.attachments.length">
			<attachment-preview-field v-for="attachment in data.attachments"
			                          :data="attachment"
			                          :allow-remove="isEditing"
			                          name="remove_attachments[]"
			                          :key="attachment.id"/>
		</div>
		<dropzone-box name="attachments[]" :label="$t('forms.upload_receipt')" url="api/receipts/attachment"
		              :max-files="0" v-if="isEditing"/>
		<div class="form-actions">
			<button class="button style-4" type="button" v-if="!isEditing" @click.prevent="toggleEdit">
				<span class="inner">{{ $t('forms.edit') }}</span>
			</button>
			<button class="button style-4" type="submit" :class="loading ? 'loading' : ''" v-else>
				<span class="inner">{{ $t('forms.save') }}</span>
			</button>
			<button class="button style-2" type="button" v-if="isEditing" @click.prevent="deleteReceipt"
			        :class="deleteLoading ? 'loading' : ''">
				<span class="inner">{{ $t('forms.delete') }}</span>
			</button>
			<button class="button style-1" type="button" @click="$store.commit('HIDE_RECEIPTS_EDIT_FORM')">
				<span class="inner">{{ $t('forms.cancel') }}</span>
			</button>
		</div>
	</form>
</template>

<script>
	import ErrorsList from "../containers/ErrorsList";
	import FieldBox from "../form-fields/FieldBox";
	import DefaultFieldBox from "../form-fields/DefaultFieldBox";
	import DropzoneBox from "../form-fields/DropzoneBox";
	import AttachmentPreviewField from "../form-fields/AttachmentPreviewField";
	import QrCodeBox from "../containers/QrCodeBox";
	import { trackEvent } from '@/analytics';

	export default {
		name: "ReceiptsEditForm",
		props: ['data'],
		components: {
			ErrorsList,
			FieldBox,
			DefaultFieldBox,
			DropzoneBox,
			AttachmentPreviewField,
			QrCodeBox,
		},
		data() {
			return {
				isEditing: false,
				loading: false,
				deleteLoading: false,
				errors: false,
				items: this.data.items ? this.data.items.map(i => ({ ...i })) : [],
			}
		},
		computed: {
			defaultCurrency() {
				return this.$store.state.app.appType === 'insanon' ? 'CZK' : 'EUR';
			},
			currencyOptions() {
				const all = [
					{ value: 'EUR', title: 'EUR' },
					{ value: 'CZK', title: 'CZK' },
					{ value: 'USD', title: 'USD' },
					{ value: 'HUF', title: 'HUF' },
					{ value: 'PLN', title: 'PLN' },
				];
				if (this.$store.state.app.appType === 'insanon') {
					return [all[1], all[0], all[2], all[3], all[4]];
				}
				return all;
			},
		},
		methods: {
			toggleEdit() {
				this.isEditing = true;
			},
			addItem() {
				this.items.push({ name: '', price: '', quantity: 1, vat_rate: '' });
			},
			removeItem(index) {
				this.items.splice(index, 1);
			},
			async submitForm() {
				const form = this.$refs.form;
				const formData = new FormData(form);

				this.loading = true;
				this.$store.commit('SET_APP_LOADING');
				trackEvent('form_submit', { form_name: 'receipt_edit', module: 'receipts' });
				try {
					const response = await this.axios.post(`/api/receipts/${this.data.id}`, formData);
					this._submitSuccess = true;
					trackEvent('form_submit_success', { form_name: 'receipt_edit', module: 'receipts' });
					this.$store.commit('UPDATE_RECEIPT', response.data.receipt);
					this.errors = false;
					// close the form after save (like documents) — the sidebar's receiptEditData
					// still points at the stale pre-save receipt, so keeping it open would re-fetch
					// just-deleted attachments; reopening pulls the fresh receipt from the list
					this.$store.commit('HIDE_RECEIPTS_EDIT_FORM');
				} catch (error) {
					trackEvent('form_submit_error', {
						form_name: 'receipt_edit',
						module: 'receipts',
						error_message: error.response && error.response.data ? error.response.data.message : error.message,
						error_status: error.response ? String(error.response.status) : null,
					});
					this.$swal.fire({
						icon: 'error',
						title: 'Oops',
						text: error.response?.data?.message || this.$t('general.default_error'),
					});
					if (error.response?.data?.errors) {
						this.errors = error.response.data.errors;
					}
				} finally {
					this.loading = false;
					this.$store.commit('UNSET_APP_LOADING');
				}
			},
			async deleteReceipt() {
				const result = await this.$swal.fire({
					icon: 'question',
					title: this.$t('general.delete_confirm_title'),
					text: this.$t('receipts.delete_confirm'),
					showDenyButton: true,
					confirmButtonText: this.$t('general.yes'),
					denyButtonText: this.$t('general.no'),
				});

				if (!result.isConfirmed) return;

				this.deleteLoading = true;
				this.$store.commit('SET_APP_LOADING');
				try {
					await this.axios.post(`/api/receipts/${this.data.id}`, { _method: 'DELETE' });
					this.$store.commit('REMOVE_RECEIPT', { id: this.data.id });
					this.$store.commit('HIDE_RECEIPTS_EDIT_FORM');
				} catch (error) {
					this.$swal.fire({
						icon: 'error',
						title: 'Oops',
						text: error.response?.data?.message || this.$t('general.default_error'),
					});
				} finally {
					this.deleteLoading = false;
					this.$store.commit('UNSET_APP_LOADING');
				}
			},
		},
		mounted() {
			this._submitSuccess = false;
			trackEvent('form_open', { form_name: 'receipt_edit', module: 'receipts' });
		},
		beforeDestroy() {
			if (!this._submitSuccess) {
				trackEvent('form_close', { form_name: 'receipt_edit', module: 'receipts' });
			}
		},
	}
</script>
<style lang="scss">
.receipts-form {
	.qr-preview {
		display: flex;
		justify-content: center;
	}
}
</style>
<style scoped lang="scss">
	.receipts-form {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	}

	.icon {
		text-align: center;
		font-size: 21px;
		margin-bottom: 10px;
	}

	.form-row {
		margin-bottom: 15px;

		&:last-child {
			margin-bottom: 0;
		}

		&.two-columns {
			display: flex;
			justify-content: space-between;
			flex-direction: row;

			.field-box {
				width: calc(50% - 8px);
			}
		}
	}

	.items-fieldset {
		margin-bottom: 15px;
	}

	.items-header {
		display: grid;
		grid-template-columns: 2fr 1fr 1fr 1fr 30px;
		gap: 6px;
		margin-bottom: 6px;

		small {
			font-size: 11px;
			opacity: .6;
		}
	}

	.item-row {
		display: grid;
		grid-template-columns: 2fr 1fr 1fr 1fr 30px;
		gap: 6px;
		margin-bottom: 6px;

		input {
			background: rgba(255, 255, 255, .06);
			border: 1px solid rgba(255, 255, 255, .1);
			border-radius: 4px;
			padding: 8px;
			color: inherit;
			font-size: 13px;
			width: 100%;

			&:disabled {
				opacity: .6;
			}

			&::placeholder {
				font-weight: 400;
			}
		}
	}

	.remove-item-btn {
		background: transparent;
		border: none;
		cursor: pointer;
		color: var(--font-color);
		font-size: 14px;
		padding: 0;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.add-item-btn {
		width: 100%;
	}

	.form-actions {
		margin-top: auto;
		display: flex;
		flex-direction: column;

		.button {
			margin-bottom: 10px;

			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	@media($mobileS) {
		.form-actions {
			margin-top: 25px;
		}
		.form-row {
			margin-bottom: 10px;

			&.two-columns {
				flex-direction: column;

				.field-box {
					width: 100%;
					margin-bottom: 10px;

					&:last-child {
						margin-bottom: 0;
					}
				}
			}
		}
	}
</style>
